import { faChevronDown, faChevronRight, faDice } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useImageStyles } from "../../../hooks/useImageStyles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EffectThemeDropdown from "../../effectTheme/effectThemeDropdown";
import { aspectRatioList} from "../../../utils/images/imageConvertFormats";
import { InfoPopoverButton } from "../ImageStudio/InfoButton";
import { CfgInfoElement, QualityInfoElement, SamplerInfoElement, SamplingStepsInfoElement, SchedulerInfoElement, SeedInfoElement } from "../../../utils/aiModels/infotexts";
import EffectThemeRange from "../../effectTheme/effectThemeRange";
import { qualityList, samplerList, schedulerList } from "../../../types/ai/api";
import { Form } from "react-bootstrap";
import { handleGetRandomNumber } from "../../../utils/calculations";
import EffectThemeInput from "../../effectTheme/effectThemeInput";
import { SettingOptionsProps } from "../../../types/miniTools/generations";

export const SettingAIOptions = ({
    aspectRatio,
    style,
    sampler,
    samplingSteps,
    scheduler,
    seed,
    cfg,
    quality,
    seedRandomActive,
    setSeedRandomActive,
    onInputsChange
}: SettingOptionsProps) => {
    const { t } = useTranslation(['generation','imageStudioSettings']);
    const { stylesList, styleFilterGroups, renderStyleDropdownItem } = useImageStyles();
    const [showSettings, setShowSettings] = useState<boolean>(false);

    return(
        <>
        <div className='row mb-2' data-tour="generation-settings">
            <button
                className="btn btn-transparent m-0 p-0 btn-lg text-start"
                onClick={() => setShowSettings(!showSettings)}
            >
                <FontAwesomeIcon
                    icon={showSettings ? faChevronDown : faChevronRight}
                    className="me-2"
                />
                {t('promptSettings')}
            </button>
        </div>
        {
            showSettings
            ?   <>
                    <div className='row m-0 ps-3 ms-1'>
                        <p className='test-small text-secondary m-0'>{t('basic')}</p>
                    </div>
                    <div className='row ps-3 m-0 ms-1'>
                        { aspectRatio !== undefined && (
                            <div className='col-auto'>
                                <div className="mb-1">{t('aspectRatio')}</div>
                                <EffectThemeDropdown
                                    className="w-100 mb-2"
                                >
                                    <EffectThemeDropdown.Toggle
                                        id="dropdown-ai-model"
                                        className="w-100"
                                    >
                                        {t(`imageStudioSettings:aspectRatio.${aspectRatio.replaceAll(' ','').replace(':','')}`)}
                                    </EffectThemeDropdown.Toggle>

                                    <EffectThemeDropdown.Menu 
                                        className="p-1 w-100"
                                        maxHeight={420}
                                        style={{ zIndex: 999999 }}
                                    >
                                        {aspectRatioList.map((item, index) => (
                                        <EffectThemeDropdown.Item
                                            key={index}
                                            onClick={()=> onInputsChange?.(prev => ({...prev,'aspectRatio': item}))}
                                            active={item === aspectRatio}
                                        >
                                            {t(`imageStudioSettings:aspectRatio.${item.replaceAll(' ','').replace(':','')}`)}
                                        </EffectThemeDropdown.Item>
                                        ))}
                                    </EffectThemeDropdown.Menu>
                                </EffectThemeDropdown>
                            </div>
                        )}
                        <div className='col-auto'>
                            <div className="mb-1">{t('style')}</div>
                            <EffectThemeDropdown.SearchFilter
                                id="dropdown-style-model"
                                className="w-100 mb-2"
                                selectedLabel={stylesList.filter(x => x.item === style).length > 0 ? stylesList.filter(x => x.item === style)[0].name : ''}
                                items={stylesList}
                                selectedItem={stylesList.find(x => x.item === style) ?? null}
                                onSelect={(x) => {
                                    onInputsChange?.(prev => ({...prev, "style": x.item}))
                                }}
                                getItemKey={(x) => x.item}
                                getItemSearchText={(x) =>`${x.name ?? ""} ${x.item} ${(x.category ?? []).join(" ")}`}
                                renderItem={renderStyleDropdownItem}
                                filterGroups={styleFilterGroups}
                                searchPlaceholder={t('placeholder.searchStyle')}
                                resetKey={"create-image"}
                                maxHeight={420}
                            />
                        </div>
                    </div>
                    <div className='row m-0 ps-3 ms-1'>
                        <p className='test-small text-secondary m-0'>{t('advanced')}</p>
                    </div>
                    <div className='row ps-3 m-0 ms-1'>
                        { samplingSteps !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0">
                                    <div className="col-auto mb-1 ps-0"> {t('samplingSteps')}</div>
                                    <div className="col">{samplingSteps}</div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <SamplingStepsInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <EffectThemeRange
                                    min={1}
                                    max={40}
                                    step={1}
                                    name="samplingSteps"
                                    value={samplingSteps}
                                    onChange={val => onInputsChange?.(prev => ({...prev,'samplingSteps': val}))}
                                    minDescription={t('range.fast')}
                                    maxDescription={t('range.slow')}
                                    className="mb-2"
                                />
                            </div>
                        )}
                        {   cfg !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0 align-items-center">
                                    <div className="col-auto mb-1 ps-0">
                                        {t('cfgScaleGuidance')}
                                    </div>
                                    <div className="col text-end">
                                        {cfg}
                                    </div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <CfgInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <EffectThemeRange
                                    min={0}
                                    max={20}
                                    step={0.1}
                                    name="cfg"
                                    value={cfg}
                                    onChange={val => onInputsChange?.(prev => ({...prev,'cfg': val}))}
                                    minDescription={t('range.creative')}
                                    maxDescription={t('range.exact')}
                                    className="mb-2"
                                />
                            </div>
                        )}
                        {   sampler !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0">
                                    <div className="col-auto mb-1 ps-0">{t('sampler')}</div>
                                    <div className="col"></div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <SamplerInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <EffectThemeDropdown className="w-100 mb-2">
                                    <EffectThemeDropdown.Toggle
                                        id="dropdown-sampler"
                                        className="w-100"
                                    >
                                        {sampler}
                                    </EffectThemeDropdown.Toggle>

                                    <EffectThemeDropdown.Menu className="p-1 w-100">
                                        {samplerList.map((item, index) => (
                                        <EffectThemeDropdown.Item
                                            key={index}
                                            onClick={()=> onInputsChange?.(prev => ({...prev, 'sampler': item}))}
                                            active={item === sampler}
                                        >
                                            {item}
                                        </EffectThemeDropdown.Item>
                                        ))}
                                    </EffectThemeDropdown.Menu>
                                </EffectThemeDropdown>
                            </div>
                        )}
                        {   scheduler !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0">
                                    <div className="col-auto mb-1 ps-0">{t('scheduler')}</div>
                                    <div className="col"></div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <SchedulerInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <EffectThemeDropdown className="w-100 mb-2">
                                    <EffectThemeDropdown.Toggle
                                        id="dropdown-scheduler"
                                        className="w-100"
                                    >
                                        {scheduler}
                                    </EffectThemeDropdown.Toggle>

                                    <EffectThemeDropdown.Menu className="p-1 w-100">
                                        {schedulerList.map((item, index) => (
                                        <EffectThemeDropdown.Item
                                            key={index}
                                            onClick={()=> onInputsChange?.(prev => ({...prev,'scheduler': item}))}
                                            active={item === scheduler}
                                        >
                                            {item}
                                        </EffectThemeDropdown.Item>
                                        ))}
                                    </EffectThemeDropdown.Menu>
                                </EffectThemeDropdown>
                            </div>
                        )}
                        {   quality !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0">
                                    <div className="col-auto mb-1 ps-0">{t('quality')}</div>
                                    <div className="col"></div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <QualityInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <EffectThemeDropdown className="w-100 mb-2">
                                    <EffectThemeDropdown.Toggle
                                        id="dropdown-quality"
                                        className="w-100"
                                    >
                                        {quality}
                                    </EffectThemeDropdown.Toggle>

                                    <EffectThemeDropdown.Menu className="p-1 w-100">
                                        {qualityList.map((item, index) => (
                                        <EffectThemeDropdown.Item
                                            key={index}
                                            onClick={()=> onInputsChange?.(prev => ({...prev,'quality': item}))}
                                            active={item === quality}
                                        >
                                            {item}
                                        </EffectThemeDropdown.Item>
                                        ))}
                                    </EffectThemeDropdown.Menu>
                                </EffectThemeDropdown>
                            </div>
                        )}
                        {   seed !== undefined && (
                            <div className='col-auto'>
                                <div className="row m-0 p-0">
                                    <div className="col-auto mb-1 ps-0">{t('seed')}</div>
                                    <div className="col"></div>
                                    <div className="col-auto mb-1 p-0">
                                        <InfoPopoverButton
                                            width={320}
                                            placement="right"
                                        >
                                            <SeedInfoElement/>
                                        </InfoPopoverButton>
                                    </div>
                                </div>
                                <div className="row m-0 p-0">
                                    <div className="col ps-0">
                                        <EffectThemeInput
                                            type="number"
                                            name="seed"
                                            value={seed}
                                            min={-1}
                                            max={999999}
                                            step={1}
                                            inputClassName="mb-2"
                                            onValueChange={val => {
                                                onInputsChange?.(prev => ({ ...prev, ['seed']: Number(val) }));
                                            }}
                                        />
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-transparent m-0 p-0"
                                            title={t('getRandomSeed')}
                                            onClick={() => onInputsChange?.(p => ({ ...p, seed: handleGetRandomNumber() }))}
                                        >
                                            <FontAwesomeIcon
                                                icon={faDice}
                                            />
                                        </button>
                                    </div>
                                </div>
                                <div className="row m-0 p-0">
                                    <div className="ps-0">
                                        <Form.Check
                                            label={t('changeSeedAuto')}
                                            type="checkbox"
                                            title={t('changeSeedAutoHint')}
                                            checked={seedRandomActive}
                                            onChange={() => {
                                                setSeedRandomActive?.(prev => !prev);
                                            }}
                                            style={{ fontSize: 16 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            :   <></>
        }
        </>
    );
};